import { AuthorizationParams, RedirectLoginOptions } from '@auth0/auth0-react';
import { Auth0ApplicationID, Auth0FrontendUser, Auth0OrganizationData, Auth0OrganizationID, Auth0OrganizationName, P2P_ORG_NAME, PRE_PROD_ORG_NAMES } from '@gr/shared/models';
import { isPreProdOrg } from '@gr/shared/utils';
import { LoadingIndicator } from '@Wonder-Cave/ui';
import { orderBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { AUTH0_MAIN_CONFIG } from './contexts/Auth0ConfigContext';
import { useMembership } from './contexts/MembershipContext';
import { useAuth0UserData } from './hooks/useAuth0Custom';
import { Auth0FrontendConfig, getSubDomain } from './providers/auth0-data.provider';
import { getProductAuth0AppId } from './providers/auth0-product.provider';
import { endSession } from './providers/auth0-recovery.provider';
import { Auth0LocalStorage, getAuth0LocalStorage, getCachedOrganizations, getCachedSelectedOrgName } from './providers/auth0-storage.provider';
import { AUTH0_AUDIENCE, createAuth0Scope } from './providers/auth0.service';

export interface IAppMainProps {
  targetOrgId: Auth0OrganizationID | undefined;
  targetOrgName: Auth0OrganizationName | undefined;
  setAuth0Config: (config: Auth0FrontendConfig) => void;
}

// Main-provider params: AUTH0_MAIN_CONFIG.clientId === getProductAuth0AppId(), so a renewal
// with these repopulates exactly the cache keys findP2pSession looks up
const MAIN_AUTHORIZATION_PARAMS: AuthorizationParams = {
  organization: AUTH0_MAIN_CONFIG.organizationId,
  audience: AUTH0_AUDIENCE,
  scope: createAuth0Scope({ orgName: AUTH0_MAIN_CONFIG.organization }),
};

const MAIN_REDIRECT_OPTIONS: RedirectLoginOptions = {
  authorizationParams: MAIN_AUTHORIZATION_PARAMS,
};

const TERMINAL_RENEWAL_ERRORS = ['login_required', 'consent_required', 'missing_refresh_token', 'invalid_grant', 'mfa_required'];

const AppMain = ({
  targetOrgId,
  targetOrgName,
  setAuth0Config,
}: IAppMainProps): JSX.Element => {
  const {
    isLoading: isAuth0Loading,
    isAuthenticated: isLoggedIn,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0UserData();

  const { setOrganizations, setAuth0LocalStorage } = useMembership();

  // Each effect run (and unmount) bumps the generation; in-flight runs abort when stale.
  // Structurally prevents duplicate bootstraps and post-unmount work.
  const runGeneration = useRef(0);
  const hasRetriedTransientFailure = useRef(false);
  const endSessionFailureRetries = useRef(0);
  const [applySessionEpoch, setApplySessionEpoch] = useState(0);

  const isLocal = window.location.origin.includes('localhost');

  // Get organizations from user data or cache (for hasAllP2PTenants users)
  const getUserOrganizations = (auth0User: Auth0FrontendUser): Auth0OrganizationData[] => {
    const hasAllP2PTenants = auth0User?.app_metadata?.hasAllP2PTenants ?? false;

    let organizations: Auth0OrganizationData[] = [];

    if (hasAllP2PTenants) {
      // For hasAllP2PTenants users, try to get from cache first, fallback to auth0User.organizations
      const cachedOrganizations = getCachedOrganizations();
      organizations = cachedOrganizations ?? auth0User?.organizations ?? [];
    } else {
      // For regular users, use auth0User.organizations
      organizations = auth0User?.organizations ?? [];
    }

    return isLocal
      ? organizations?.filter(org => isPreProdOrg(org)) ?? []
      : organizations ?? [];
  };

  const getMatchingOrganization = (organizations: Auth0OrganizationData[]) => {
    const subDomain = getSubDomain();
    const isPreProd = PRE_PROD_ORG_NAMES.includes(subDomain?.toLowerCase()?.trim());
    const cachedSelectedOrgName = getCachedSelectedOrgName();

    const orgs = organizations
      ?.filter(org => org?.name?.toLowerCase()?.trim() !== P2P_ORG_NAME?.toLowerCase()?.trim())
      ?.filter(org => (isPreProd || isLocal) ? isPreProdOrg(org) : true)
      ?? [];

    const ordered = isLocal
      ? orderBy(orgs, org => org?.name === 'dev', 'desc')
      : orderBy(orgs, org => org?.name, 'asc');

    const envTargetOrg = isLocal ? import.meta.env.VITE_TARGET_ORG : undefined;

    const match = ordered?.find(org => org?.id?.toLowerCase()?.trim() === targetOrgId?.toLowerCase()?.trim())
      ?? (envTargetOrg ? ordered?.find(org => org?.name?.toLowerCase()?.trim() === envTargetOrg?.toLowerCase()?.trim()) : undefined)
      ?? ordered?.find(org => org?.name?.toLowerCase()?.trim() === cachedSelectedOrgName?.toLowerCase()?.trim())
      ?? ordered?.find(org => org?.name?.toLowerCase()?.trim() === targetOrgName?.toLowerCase()?.trim())
      ?? ordered?.find(org => org?.name?.toLowerCase()?.trim() === subDomain?.toLowerCase()?.trim())
      ?? ordered?.[0];

    return match;
  };

  const getLocalApiUrl = (orgName: Auth0OrganizationName) => {
    if (['dev', 'dev2'].includes(orgName)) {
      return `http://localhost:3000/dev`;
    }

    if (['staging', 'staging2'].includes(orgName)) {
      return `http://localhost:3000/staging`;
    }

    return `http://localhost:3000/${orgName}`;
  };

  // A session without a user id is indistinguishable from no session to every reader
  const findP2pSession = (auth0LocalStorage: Auth0LocalStorage) => {
    return auth0LocalStorage?.sessions?.find(session => session?.appId === getProductAuth0AppId() && !!session?.user?.user_id);
  };

  const renewMainAppSession = async () => {
    console.log('[AppMain] No p2p session in local storage, renewing token to repopulate it', { userId: auth0User?.user_id });

    await getAccessTokenSilently({
      cacheMode: 'off',
      authorizationParams: MAIN_AUTHORIZATION_PARAMS,
    });
  };

  const isStaleRun = (generation: number) => generation !== runGeneration.current;

  // A failed redirect must not strand the user on the spinner — re-fire the effect once
  // so the bootstrap (and its bounded endSession budget) gets another attempt
  const handleEndSessionFailure = (generation: number) => {
    if (isStaleRun(generation)) {
      return;
    }

    if (endSessionFailureRetries.current >= 1) {
      console.error('[AppMain] Ending the session failed repeatedly, staying on loading screen');
      return;
    }

    endSessionFailureRetries.current += 1;
    console.error('[AppMain] Failed to end session, retrying session apply');
    setApplySessionEpoch(epoch => epoch + 1);
  };

  const handleApplySessionFailure = (error: any, auth0Config: Auth0FrontendConfig | undefined, generation: number) => {
    if (isStaleRun(generation)) {
      return;
    }

    console.error('[AppMain] Failed to apply session', {
      error: error?.error ?? 'unknown',
      errorName: error?.name,
      description: error?.error_description,
    });

    const isTerminal = TERMINAL_RENEWAL_ERRORS.includes(error?.error);

    if (!isTerminal && !hasRetriedTransientFailure.current) {
      console.warn('[AppMain] Failure looks transient, retrying session apply once');
      hasRetriedTransientFailure.current = true;
      setApplySessionEpoch(epoch => epoch + 1);
      return;
    }

    endSession({ auth0Config, auth0Logout, loginWithRedirect, redirectOptions: MAIN_REDIRECT_OPTIONS })
      .catch(() => handleEndSessionFailure(generation));
  };

  const applySession = async (generation: number) => {
    if (!auth0User?.user_id) {
      console.log('[AppMain] Auth0 user id not resolved yet, skipping session apply');
      return;
    }

    let auth0Config: Auth0FrontendConfig | undefined;

    try {
      const organizations = getUserOrganizations(auth0User);
      const match = getMatchingOrganization(organizations);

      if (!match) {
        console.warn('[AppMain] No matching organization for user, staying on loading screen', {
          userId: auth0User?.user_id,
          organizationCount: organizations?.length ?? 0,
        });
        return;
      }

      if (!match.metadata) {
        console.warn('[AppMain] Matched organization has no metadata, staying on loading screen', { organizationId: match?.id });
        return;
      }

      const useLocalBackend = isLocal && import.meta.env.VITE_BACKEND_MODE !== 'remote';

      auth0Config = {
        label: match.display_name,
        organization: match.name as Auth0OrganizationName,
        organizationId: match?.id as Auth0OrganizationID,

        clientId: match.metadata.client_id as Auth0ApplicationID,
        apiUrl: useLocalBackend ? getLocalApiUrl(match.name) : match.metadata.api_url,
        websocketUrl: useLocalBackend ? 'ws://localhost:3003' : match.metadata.websocket_url,
        gsdUrl: match.metadata.gsd_url,
      };

      let auth0LocalStorage = getAuth0LocalStorage(auth0User);
      let p2pSession = findP2pSession(auth0LocalStorage);

      if (!p2pSession) {
        await renewMainAppSession();

        if (isStaleRun(generation)) {
          return;
        }

        auth0LocalStorage = getAuth0LocalStorage(auth0User);
        p2pSession = findP2pSession(auth0LocalStorage);
      }

      if (!p2pSession) {
        console.warn('[AppMain] P2p session still missing after renewal, ending session', { userId: auth0User?.user_id });
        endSession({ auth0Config, auth0Logout, loginWithRedirect, redirectOptions: MAIN_REDIRECT_OPTIONS })
          .catch(() => handleEndSessionFailure(generation));
        return;
      }

      hasRetriedTransientFailure.current = false;
      endSessionFailureRetries.current = 0;
      setAuth0LocalStorage(auth0LocalStorage);
      setOrganizations(organizations);
      setAuth0Config(auth0Config);
    } catch (error) {
      handleApplySessionFailure(error, auth0Config, generation);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const generation = ++runGeneration.current;

    applySession(generation);

    return () => {
      runGeneration.current += 1;
    };
  }, [targetOrgName, isLoggedIn, auth0User?.user_id, applySessionEpoch]);

  if (!isAuth0Loading && !isLoggedIn) {
    loginWithRedirect();
  }

  return <LoadingIndicator size={16} position="CENTER" vPosition="CENTER" />;
};

export default AppMain;
